import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import PhoneInput from './PhoneInput';
import FieldTooltip from './FieldTooltip';
import ValidationIndicator from './ValidationIndicator';
import { useAutoSave } from '../hooks/useAutoSave';
import { COUNTRIES, getStatesForCountry, hasStates } from '../utils/countryStateData';
import AddCompanyModal from './AddCompanyModal';

const AddContactModalComprehensive = ({ onClose, onSuccess, customers, vendors, backendUrl }) => {
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [tempCustomers, setTempCustomers] = useState(customers);
  
  const [formData, setFormData] = useState({
    // Mandatory
    first_name: '',
    last_name: '',
    status: 'Active',
    owner: '',
    primary_email: '',
    
    // Optional Basic
    middle_name: '',
    nickname: '',
    title: '',
    
    // Phones (JSON)
    client_direct_phone: null,
    mobile_phone: null,
    other_phone: null,
    
    // Association
    contact_type: 'customer',
    customer_id: '',
    vendor_id: '',
    company_id: '',
    
    // Organizational
    department: '',
    
    // Additional
    secondary_email: '',
    general_contact_comments: '',
    
    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    custom_city: '', // For "Other" option
    state: '',
    zip_code: '',
    country: 'United States',
    
    // Invoice (optional)
    invoice_frequency: '',
    invoice_format: 'All placements as one invoice',
    net_terms: 30,
    
    created_by: 'system'
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [duplicateEmailCheck, setDuplicateEmailCheck] = useState({ checking: false, isDuplicate: false });
  
  // Auto-save integration
  const { loadDraft, clearDraft, lastSaved } = useAutoSave('add-contact-draft', formData, true);

  // Check for duplicate email
  useEffect(() => {
    const checkDuplicateEmail = async () => {
      if (!formData.primary_email || formData.primary_email.length < 5) {
        setDuplicateEmailCheck({ checking: false, isDuplicate: false });
        return;
      }
      
      setDuplicateEmailCheck({ checking: true, isDuplicate: false });
      
      try {
        const response = await fetch(`${backendUrl}/api/contacts`);
        if (response.ok) {
          const data = await response.json();
          const existing = (data.contacts || []).find(c => 
            c.primary_email?.toLowerCase() === formData.primary_email.toLowerCase() ||
            c.email?.toLowerCase() === formData.primary_email.toLowerCase()
          );
          
          setDuplicateEmailCheck({ 
            checking: false, 
            isDuplicate: !!existing 
          });
        }
      } catch (error) {
        setDuplicateEmailCheck({ checking: false, isDuplicate: false });
      }
    };
    
    const timer = setTimeout(checkDuplicateEmail, 500);
    return () => clearTimeout(timer);
  }, [formData.primary_email, backendUrl]);
  
  // Sync tempCustomers with customers prop
  useEffect(() => {
    setTempCustomers(customers);
  }, [customers]);
  
  // Load draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft && draft.first_name) {
      setShowDraftPrompt(true);
    }
  }, []);

  // Auto-fill address when customer/vendor is selected
  useEffect(() => {
    if (formData.contact_type === 'customer' && formData.customer_id) {
      const customer = customers.find(c => c.id === formData.customer_id);
      if (customer) {
        setFormData(prev => ({
          ...prev,
          address_line1: customer.address_line_1 || customer.address_line1 || customer.address || '',
          address_line2: customer.address_line_2 || customer.address_line2 || '',
          city: customer.city || '',
          state: customer.state || '',
          zip_code: customer.zip_code || '',
          country: customer.country || 'United States'
        }));
      }
    } else if (formData.contact_type === 'vendor' && formData.vendor_id) {
      const vendor = vendors.find(v => v.id === formData.vendor_id);
      if (vendor) {
        setFormData(prev => ({
          ...prev,
          address_line1: vendor.address_line_1 || vendor.address_line1 || vendor.address || '',
          address_line2: vendor.address_line_2 || vendor.address_line2 || '',
          city: vendor.city || '',
          state: vendor.state || '',
          zip_code: vendor.zip_code || '',
          country: vendor.country || 'United States'
        }));
      }
    }
  }, [formData.contact_type, formData.customer_id, formData.vendor_id, customers, vendors]);

  const validate = () => {
    const newErrors = {};
    
    // Mandatory field validations
    if (!formData.first_name || formData.first_name.length < 1 || formData.first_name.length > 100) {
      newErrors.first_name = 'First name is required (1-100 characters)';
    }
    
    if (!formData.last_name || formData.last_name.length < 1 || formData.last_name.length > 100) {
      newErrors.last_name = 'Last name is required (1-100 characters)';
    }
    
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    
    if (!formData.owner || formData.owner.length < 1) {
      newErrors.owner = 'Owner is required';
    }
    
    if (!formData.primary_email) {
      newErrors.primary_email = 'Primary email is required';
    }
    
    if (formData.contact_type === 'customer' && !formData.customer_id) {
      newErrors.customer_id = 'Please select a customer';
    }
    
    if (formData.contact_type === 'vendor' && !formData.vendor_id) {
      newErrors.vendor_id = 'Please select a vendor';
    }
    
    // Optional field length validations
    if (formData.middle_name && formData.middle_name.length > 100) {
      newErrors.middle_name = 'Maximum 100 characters';
    }
    
    if (formData.nickname && formData.nickname.length > 100) {
      newErrors.nickname = 'Maximum 100 characters';
    }
    
    if (formData.title && formData.title.length > 200) {
      newErrors.title = 'Maximum 200 characters';
    }
    
    if (formData.department && formData.department.length > 200) {
      newErrors.department = 'Maximum 200 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        clearDraft(); // Clear draft on success
        toast.success('Contact created successfully!');
        onSuccess();
        onClose(); // Close modal after success
      } else {
        // Handle validation errors properly
        let errorMessage = 'Failed to create contact';
        
        if (result.detail) {
          if (typeof result.detail === 'string') {
            errorMessage = result.detail;
          } else if (Array.isArray(result.detail)) {
            // Pydantic validation errors - extract messages
            errorMessage = result.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else if (typeof result.detail === 'object') {
            errorMessage = JSON.stringify(result.detail);
          }
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Error creating contact: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = ['Active', 'Passive', 'DNC', 'Archive'];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={(e) => {
        // Close modal only if clicking on the overlay (not the modal content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg w-full max-w-5xl my-8"
        onClick={(e) => {
          // Prevent clicks inside modal from closing it
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add New Contact</h2>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-gray-600">Fields marked with * are mandatory</p>
              {lastSaved && (
                <p className="text-green-600 text-xs flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Auto-saved
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Draft Restore Prompt */}
        {showDraftPrompt && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span className="text-sm text-yellow-800">You have an unsaved draft</span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const draft = loadDraft();
                  if (draft) {
                    setFormData(draft);
                    toast.success('Draft restored!');
                  }
                  setShowDraftPrompt(false);
                }}
                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
              >
                Restore
              </button>
              <button
                type="button"
                onClick={() => {
                  clearDraft();
                  setShowDraftPrompt(false);
                }}
                className="px-3 py-1 bg-white text-gray-700 text-sm rounded border hover:bg-gray-50"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          
          {/* SECTION 1: Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  maxLength={100}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.first_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Middle Name</label>
                <input
                  type="text"
                  value={formData.middle_name}
                  onChange={(e) => setFormData({...formData, middle_name: e.target.value})}
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  maxLength={100}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.last_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nickname</label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                  maxLength={100}
                  placeholder="Preferred name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  maxLength={200}
                  placeholder="e.g., HR Manager, VP of Operations"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                  <FieldTooltip content="Active = Currently engaging, Passive = Not actively looking, DNC = Do Not Contact, Archive = Historical record" />
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Contact engagement status</p>
              </div>
            </div>
          </div>

          {/* SECTION 2: Company Association - SELECT FIRST */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-yellow-300 pb-2">Company Association</h3>
            <p className="text-sm text-gray-700 mb-4">Select the company first - contact information will auto-fill below</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Type <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="contact_type"
                      value="customer"
                      checked={formData.contact_type === 'customer'}
                      onChange={(e) => setFormData({...formData, contact_type: e.target.value, customer_id: '', vendor_id: ''})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Customer Contact</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="contact_type"
                      value="vendor"
                      checked={formData.contact_type === 'vendor'}
                      onChange={(e) => setFormData({...formData, contact_type: e.target.value, customer_id: '', vendor_id: ''})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Vendor Contact</span>
                  </label>
                </div>
              </div>

              {formData.contact_type === 'customer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Customer <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      required
                      value={formData.customer_id}
                      onChange={(e) => {
                        if (e.target.value === '__ADD_NEW__') {
                          setShowAddCompanyModal(true);
                        } else {
                          setFormData({...formData, customer_id: e.target.value});
                        }
                      }}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.customer_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a customer...</option>
                      <option disabled>───────────────</option>
                      {tempCustomers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.client_name || customer.company_name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowAddCompanyModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                      data-testid="add-company-from-contact-button"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add New
                    </button>
                  </div>
                  {errors.customer_id && <p className="text-red-500 text-xs mt-1">{errors.customer_id}</p>}
                </div>
              )}

              {formData.contact_type === 'vendor' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Vendor <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.vendor_id}
                    onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.vendor_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a vendor...</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.company_name}
                      </option>
                    ))}
                  </select>
                  {errors.vendor_id && <p className="text-red-500 text-xs mt-1">{errors.vendor_id}</p>}
                </div>
              )}
            </div>
          </div>

          {/* SECTION 3: Contact Information - AUTO-FILLS AFTER COMPANY SELECTION */}
          <div className="mt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Contact Information</h3>
            
            {/* Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    required
                    value={formData.primary_email}
                    onChange={(e) => setFormData({...formData, primary_email: e.target.value})}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.primary_email || duplicateEmailCheck.isDuplicate ? 'border-red-500' : 
                      formData.primary_email && /\S+@\S+\.\S+/.test(formData.primary_email) && !duplicateEmailCheck.isDuplicate ? 'border-green-500' :
                      'border-gray-300'
                    }`}
                  />
                  <ValidationIndicator 
                    isValid={formData.primary_email && /\S+@\S+\.\S+/.test(formData.primary_email) && !duplicateEmailCheck.isDuplicate && !duplicateEmailCheck.checking}
                    isInvalid={duplicateEmailCheck.isDuplicate}
                  />
                </div>
                {duplicateEmailCheck.checking && (
                  <p className="text-blue-500 text-xs mt-1">Checking for duplicates...</p>
                )}
                {duplicateEmailCheck.isDuplicate && (
                  <p className="text-yellow-600 text-xs mt-1">⚠️ A contact with this email already exists</p>
                )}
                {errors.primary_email && <p className="text-red-500 text-xs mt-1">{errors.primary_email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Email</label>
                <input
                  type="email"
                  value={formData.secondary_email}
                  onChange={(e) => setFormData({...formData, secondary_email: e.target.value})}
                  placeholder="alternate@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Phone Numbers - Compact Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <PhoneInput
                value={formData.client_direct_phone}
                onChange={(phone) => setFormData({...formData, client_direct_phone: phone})}
                label="Direct Phone"
                placeholder="Direct number"
              />
              
              <PhoneInput
                value={formData.mobile_phone}
                onChange={(phone) => setFormData({...formData, mobile_phone: phone})}
                label="Mobile Phone"
                placeholder="Mobile number"
              />
              
              <div className="md:col-span-2">
                <PhoneInput
                  value={formData.other_phone}
                  onChange={(phone) => setFormData({...formData, other_phone: phone})}
                  label="Other Phone (Optional)"
                  placeholder="Alternate number"
                />
              </div>
            </div>
            
            {/* Address - Traditional Top-to-Bottom Layout (Option 1) */}
            <div className="space-y-3 mt-6">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Address</p>
                {(formData.customer_id || formData.vendor_id) && (
                  <span className="text-xs text-green-600 font-medium">✓ Auto-filled from company</span>
                )}
              </div>
              
              {/* Address Line 1 */}
              <input
                type="text"
                placeholder="Address Line 1"
                value={formData.address_line1}
                onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {/* Address Line 2 */}
              <input
                type="text"
                placeholder="Address Line 2 (Optional)"
                value={formData.address_line2}
                onChange={(e) => setFormData({...formData, address_line2: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {/* City - Simple Text Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  placeholder="Enter city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* State/Province - Smart Dropdown based on Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State/Province</label>
                {hasStates(formData.country) ? (
                  <select
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">Select {formData.country === 'United States' ? 'state' : formData.country === 'Canada' ? 'province' : 'region'}...</option>
                    {getStatesForCountry(formData.country).map(state => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Enter state/province"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
              </div>
              
              {/* Zip/Postal Code - Simple Text Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zip/Postal Code</label>
                <input
                  type="text"
                  placeholder="Enter zip code"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Country - Dropdown (Determines State options) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value, state: ''})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Changing country will reset state selection</p>
              </div>
            </div>
          </div>

          {/* SECTION 4: Organizational Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Organizational Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  maxLength={200}
                  placeholder="e.g., Human Resources, IT"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.owner}
                  onChange={(e) => setFormData({...formData, owner: e.target.value})}
                  placeholder="Who manages this contact?"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.owner ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.owner && <p className="text-red-500 text-xs mt-1">{errors.owner}</p>}
              </div>
            </div>
          </div>

          {/* SECTION 5: Comments */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Additional Notes</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">General Contact Comments</label>
              <textarea
                value={formData.general_contact_comments}
                onChange={(e) => setFormData({...formData, general_contact_comments: e.target.value})}
                rows={4}
                placeholder="Add any relevant notes about this contact..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white z-10">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                onClose();
              }}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
              }}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {submitting ? 'Creating Contact...' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Inline Company Creation Modal */}
      {showAddCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100]">
          <AddCompanyModal
            isOpen={showAddCompanyModal}
            onClose={() => setShowAddCompanyModal(false)}
            onSuccess={async (newCompany) => {
              // Refresh the customer list
              try {
                const response = await fetch(`${backendUrl}/api/customers`);
                if (response.ok) {
                  const data = await response.json();
                  const newCustomers = data.customers || [];
                  setTempCustomers(newCustomers);
                  
                  // Auto-select the newly created company
                  if (newCompany && newCompany.id) {
                    setFormData({...formData, customer_id: newCompany.id});
                    toast.success('Company created and selected!');
                  }
                }
              } catch (error) {
                console.error('Error refreshing customers:', error);
              }
              setShowAddCompanyModal(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AddContactModalComprehensive;
